import React, { useState, useEffect } from "react";
import { Select, DatePicker } from "antd";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import CeoDashboardService, {
  ChartDataResponse,
  DashTransactionsResponse,
} from "./ceo-dashboard.service";
import DashboardCards from "./components/dashboard-cards";
import DashboardChart from "./components/dashboard-chart";
import TransactionsTable from "./components/transactions-table";
import InflowsOutflowsCards from "./components/inflows-outflows-cards";
import InflowsOutflowsTable from "./components/inflows-outflows-table";
import InflowsOutflowsChart from "./components/inflows-outflows-chart";
import FundSubscriptionsCards from "./components/fund-subscriptions-cards";
import FundSubscriptionsTable from "./components/fund-subscriptions-table";
import FundSubscriptionsChart from "./components/fund-subscriptions-chart";
import { useSelector } from "react-redux";

const { RangePicker } = DatePicker;
const { Option } = Select;

const CeoDashboard: React.FC = () => {
  const user = useSelector((state: any) => state.user);
  const [chartData, setChartData] = useState<ChartDataResponse | null>(null);
  const [transactionsData, setTransactionsData] =
    useState<DashTransactionsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [transactionType, setTransactionType] = useState<string>("1");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().subtract(1, "year").add(1, "month"),
    dayjs(),
  ]);
  const [currentSearch, setCurrentSearch] = useState<string>("");
  const [currentFilters, setCurrentFilters] = useState<any>({});

  const ceoDashboardService = new CeoDashboardService();

  useEffect(() => {
    fetchDashboardData();
  }, [transactionType, dateRange]);

  const fetchDashboardData = async () => {
    const [startDate, endDate] = dateRange;

    // Check if both dates are required and present
    if (!startDate || !endDate) {
      return;
    }

    try {
      setLoading(true);

      const dateFrom = startDate.format("YYYY-MM");
      const dateTo = endDate.format("YYYY-MM");

      // Fetch chart data
      // For Monthly Inflows and Outflows, don't filter by transaction type
      // For Monthly transaction amounts by type, only filter if a specific type is selected
      let trxTypeParam;
      if (transactionType === "2") {
        // Monthly Inflows and Outflows - get all data
        trxTypeParam = undefined;
      } else if (transactionType === "1") {
        // Monthly transaction amounts by type - get all data
        trxTypeParam = undefined;
      } else {
        // Specific transaction type selected
        trxTypeParam = transactionType;
      }

      const chartResponse =
        await ceoDashboardService.getDashboardTransactionsSummaryMonth({
          dateFrom,
          dateTo,
          trxType: trxTypeParam,
        });
      if (chartResponse) {
        setChartData(chartResponse);
      }

      // Fetch transactions data
      const transactionsResponse =
        await ceoDashboardService.getDashboardTransactions({
          dateFrom,
          dateTo,
          page: 1,
          pageSize: 10,
        });
      if (transactionsResponse) {
        setTransactionsData(transactionsResponse);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactionsListData = async (
    page: number = 1,
    pageSize: number = 10,
    searchWord: string = "",
    filters: any = {}
  ) => {
    const [startDate, endDate] = dateRange;
    if (!startDate || !endDate) {
      return;
    }

    const dateFrom = startDate.format("YYYY-MM");
    const dateTo = endDate.format("YYYY-MM");

    // Process filters
    let transactionType = "";
    let status = "";

    if (filters.filters) {
      if (filters.filters.transactiontype) {
        transactionType = filters.filters.transactiontype[0];
      }
      if (filters.filters.status) {
        status = filters.filters.status[0];
      }
    }

    try {
      setLoading(true);
      const transactionsResponse =
        await ceoDashboardService.getDashboardTransactions({
          dateFrom,
          dateTo,
          page: page,
          pageSize: pageSize,
          search: searchWord,
          transactionType,
          status,
        });
      if (transactionsResponse) {
        setTransactionsData(transactionsResponse);
      }
    } catch (error) {
      console.error("Error fetching transactions data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    fetchTransactionsListData(page, pageSize, currentSearch, currentFilters);
  };

  const handleSearchChange = (searchWord: string) => {
    setCurrentSearch(searchWord);
    fetchTransactionsListData(1, 10, searchWord, currentFilters);
  };

  const handleFilterChange = (filters: any) => {
    setCurrentFilters(filters);
    fetchTransactionsListData(1, 10, currentSearch, filters);
  };

  return (
    <div className="w-full inline-flex flex-col justify-start items-start gap-5">
      <div className="self-stretch flex flex-col justify-start items-start gap-5">
        <div className="self-stretch flex flex-col justify-start items-start gap-5">
          <div className="self-stretch inline-flex justify-between items-center">
            <p className="justify-center text-zinc-800 text-sm font-medium">
              Dashboard
            </p>
            <div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-2.5">
              <div className="w-full sm:w-72 px-3 h-10 bg-white rounded-full outline outline-1 outline-offset-[-1px] outline-blue-500 flex justify-between items-center">
                <Select
                  value={transactionType}
                  onChange={setTransactionType}
                  placeholder="Monthly transaction amounts by type"
                  bordered={false}
                  className="w-full"
                >
                  <Option value="1">Monthly transaction amounts by type</Option>
                  <Option value="2">Monthly Inflows and Outflows</Option>
                  <Option value="3">Monthly fund subscriptions </Option>
                </Select>
              </div>
              <div className="w-full sm:w-60 px-3 h-10 bg-white rounded-full outline outline-1 outline-offset-[-1px] outline-blue-500 flex justify-start items-center gap-2">
                <RangePicker
                  picker="month"
                  value={dateRange}
                  onChange={setDateRange}
                  format="YYYY-MM"
                  placeholder={["Start", "End"]}
                  inputReadOnly={true}
                  bordered={false}
                  className="w-full text-xs"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {transactionType === "2" ? (
        // Monthly Inflows and Outflows View
        <>
          <div className="self-stretch flex flex-col lg:flex-row justify-start items-start gap-5">
            <InflowsOutflowsChart
              chartData={chartData}
              loading={loading}
              dateRange={dateRange}
            />
            <InflowsOutflowsCards chartData={chartData} loading={loading} />
          </div>
          <InflowsOutflowsTable
            chartData={chartData}
            loading={loading}
            dateRange={dateRange}
          />
        </>
      ) : transactionType === "3" ? (
        // Monthly Fund Subscriptions View
        <>
          <div className="self-stretch flex flex-col lg:flex-row justify-start items-start gap-5">
            <FundSubscriptionsChart
              transactionsData={transactionsData}
              loading={loading}
              dateRange={dateRange}
            />
            <FundSubscriptionsCards
              transactionsData={transactionsData}
              loading={loading}
            />
          </div>
          <FundSubscriptionsTable
            transactionsData={transactionsData}
            loading={loading}
            dateRange={dateRange}
          />
        </>
      ) : (
        // Default Monthly transaction amounts by type View
        <>
          <div className="self-stretch flex flex-col lg:flex-row justify-start items-start gap-5">
            <DashboardChart
              chartData={chartData}
              loading={loading}
              transactionType={transactionType === "1" ? "" : transactionType}
              dateRange={dateRange}
            />
            <DashboardCards chartData={chartData} loading={loading} />
          </div>
          {user?.permissions?.includes("dashboard_trx") && (
            <TransactionsTable
              data={transactionsData}
              loading={loading}
              onPageChange={handlePageChange}
              onSearchChange={handleSearchChange}
              onFilterChange={handleFilterChange}
              currentFilters={currentFilters}
            />
          )}
        </>
      )}
    </div>
  );
};

export default CeoDashboard;
